import { scaleLinear } from 'd3';
import React, { memo, useCallback, useMemo } from 'react';
import GaugeArc from './GaugeArc';
import InvertedTriangle from './InvertedTriangle';
import Label from './Label';
import Pointer from './Pointer';
import Uom from './Uom';

const defaultSize = 250;

const GaugeChart = ({
	height,
	width,
	disabled,
	value = -1,
	min = 0,
	max = 100,
	endAngle = 135,
	startAngle = -135,
	tickCount = 5,
	labelTickCount= 5,
	segments = [{ min: 0, max: 1, color: 'skyblue' }],
	labelProps = {
		offsetText: -2.5
	},
	uom = '',
	title = '',
	uomProps = {
		offsetText: -2.5
	},
	pointerLabel = '',
	showGaugeOrigin=false,
	gradient=false,
	tickValue,
	tickLabelDecimals=0,
	...rest
}) => {
	const gaugeOrigin = {
		x: 140,
		y: 140
	};

	const valueScale = useCallback(scaleLinear().domain([min, max]).range([0, 1]), [min, max]);
	const angleOffset = ((min) / (max - min)) * (endAngle - startAngle);
	const [arcSegments] = useMemo(() => {
		const total = max - min;
		if (Array.isArray(segments)) {
			const hasNonRatioValues = segments.some(({ min, max }) => min > 1 || max > 1);
			if (hasNonRatioValues) {
				const segmentsArr =  segments.map(({ min: segmentMin, max: segmentMax, ...segment }) => ({
					min: segmentMin / total,
					max: segmentMax / total,
					...segment
				}));
				return [segmentsArr];
			} else {
				return [segments];
			}
		}
		return [[]];
	}, [segments, max, min]);

	/** Value scaled to [0,1] */
	const valueRef = useMemo(() => valueScale(value), [value, valueScale]);
	let tickCountVal = tickValue > 0 ? Math.round((max -min) / tickValue) : tickCount;
	if (tickCountVal % 2 === 0) {
		tickCountVal++;
	}
	return (
		<>
			<svg
				className="gauge"
				width={width ? width : height ? height : defaultSize}
				height={height ? height : width ? width : defaultSize}
				viewBox={'0 0 280 280'}
				style={{
					transition: 'all 0.25s 0.25s'
				}}
				{...rest}
			>
				<GaugeArc
					strokeWidth={0}
					center={gaugeOrigin}
					maxAngle={endAngle}
					minAngle={startAngle}
					opacity={disabled ? 0.25 : undefined}
				/>
				<Label
					disabled={disabled}
					center={gaugeOrigin}
					tickCount={tickCountVal}
					labelTickCount={labelTickCount}
					min={min}
					max={max}
					maxAngle={endAngle}
					minAngle={startAngle}
					tickLabelDecimals={tickLabelDecimals}
					{...labelProps}
				/>
				{arcSegments.map(({ min, max, color, node }, idx) => (
					<g key={`arcsegment-${idx}`}>
						{typeof node === 'function' ? node(disabled) : node}
						<GaugeArc
							key={`gauge-arcsegment-${idx}`}
							inset={12}
							min={min}
							max={max}
							stroke={
								disabled && !node ? `rgba(${idx * 15},${idx * 15},${idx * 15}, ${idx * 0.1 + 0.1})` : color
							}
							strokeWidth={20}
							center={gaugeOrigin}
							maxAngle={endAngle - angleOffset}
							minAngle={startAngle - angleOffset}
						/>
					</g>
				))}
				{showGaugeOrigin && <InvertedTriangle center={gaugeOrigin} disabled={disabled} />}
				<Pointer
					value={disabled ? -0.025 : valueRef}
					center={gaugeOrigin}
					disabled={disabled}
					maxAngle={endAngle}
					minAngle={startAngle}
					tooltip={pointerLabel ? pointerLabel : value}
				/>
				<Uom value={uom} title={title} disabled={disabled} center={gaugeOrigin} {...uomProps} />
			</svg>
		</>
	);
};

export default memo(GaugeChart);
